<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  fieldErrors: {
    type: Object,
    default: () => ({}),
  },
  supportedTemplateLanguages: {
    type: Array,
    required: true,
  },
  languageLabels: {
    type: Object,
    required: true,
  },
  editMode: {
    type: Boolean,
    default: false,
  },
  categoryEditable: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const update = field => val =>
  emit('update:modelValue', { ...props.modelValue, [field]: val });

const nameVal = computed({
  get: () => props.modelValue.name,
  set: update('name'),
});
const categoryVal = computed({
  get: () => props.modelValue.category,
  set: update('category'),
});
const languageVal = computed({
  get: () => props.modelValue.language,
  set: update('language'),
});
const formatVal = computed({
  get: () => props.modelValue.parameterFormat ?? 'positional',
  set: update('parameterFormat'),
});

const isNameLocked = computed(() => props.editMode);
const isLanguageLocked = computed(() => props.editMode);
const isCategoryLocked = computed(
  () => props.editMode && !props.categoryEditable
);
const isFormatLocked = computed(() => props.editMode);

// Unicode escapes only work in JS strings, not in template text nodes
const positionalHint = '\u007b\u007b1\u007d\u007d, \u007b\u007b2\u007d\u007d';
const namedHint = '\u007b\u007bname\u007d\u007d';
</script>

<template>
  <div class="space-y-4">
    <div>
      <label class="block text-xs font-medium text-n-slate-11 mb-1">
        {{ t('INBOX_MGMT.TEMPLATES.CREATE.NAME') }}
      </label>
      <input
        v-model="nameVal"
        type="text"
        pattern="[a-z0-9_]+"
        :disabled="isNameLocked"
        class="w-full h-9 px-3 rounded-lg outline outline-1 outline-n-weak bg-transparent text-sm text-n-slate-12 focus:outline-n-brand disabled:opacity-60 disabled:cursor-not-allowed"
        :placeholder="t('INBOX_MGMT.TEMPLATES.CREATE.NAME_PH')"
      />
      <p v-if="isNameLocked" class="text-[11px] text-n-slate-10 mt-1">
        {{ t('INBOX_MGMT.TEMPLATES.EDIT.NAME_LOCKED') }}
      </p>
      <p v-else class="text-[11px] text-n-slate-10 mt-1">
        {{ t('INBOX_MGMT.TEMPLATES.CREATE.NAME_HELP') }}
      </p>
      <p v-if="fieldErrors.name" class="text-[11px] text-n-ruby-9 mt-1">
        {{ fieldErrors.name }}
      </p>
    </div>

    <div>
      <label class="block text-xs font-medium text-n-slate-11 mb-1">
        {{ t('INBOX_MGMT.TEMPLATES.CREATE.CATEGORY') }}
      </label>
      <select
        v-model="categoryVal"
        :disabled="isCategoryLocked"
        class="w-full h-9 px-3 rounded-lg outline outline-1 outline-n-weak bg-n-background text-sm text-n-slate-12 focus:outline-n-brand disabled:opacity-60 disabled:cursor-not-allowed"
      >
        <option value="UTILITY">
          {{ t('INBOX_MGMT.TEMPLATES.CREATE.CATEGORIES.UTILITY') }}
        </option>
        <option value="MARKETING">
          {{ t('INBOX_MGMT.TEMPLATES.CREATE.CATEGORIES.MARKETING') }}
        </option>
        <option value="AUTHENTICATION">
          {{ t('INBOX_MGMT.TEMPLATES.CREATE.CATEGORIES.AUTHENTICATION') }}
        </option>
      </select>
      <p v-if="isCategoryLocked" class="text-[11px] text-n-slate-10 mt-1">
        {{ t('INBOX_MGMT.TEMPLATES.EDIT.CATEGORY_LOCKED') }}
      </p>
    </div>

    <div>
      <label class="block text-xs font-medium text-n-slate-11 mb-1">
        {{ t('INBOX_MGMT.TEMPLATES.CREATE.LANGUAGE') }}
      </label>
      <select
        v-model="languageVal"
        :disabled="isLanguageLocked"
        class="w-full h-9 px-3 rounded-lg outline outline-1 outline-n-weak bg-n-background text-sm text-n-slate-12 focus:outline-n-brand disabled:opacity-60 disabled:cursor-not-allowed"
      >
        <option
          v-for="code in supportedTemplateLanguages"
          :key="code"
          :value="code"
        >
          {{ `${languageLabels[code] || code} (${code})` }}
        </option>
      </select>
      <p v-if="isLanguageLocked" class="text-[11px] text-n-slate-10 mt-1">
        {{ t('INBOX_MGMT.TEMPLATES.EDIT.LANGUAGE_LOCKED') }}
      </p>
    </div>

    <!-- Variable format toggle -->
    <div>
      <label class="block text-xs font-medium text-n-slate-11 mb-1">
        {{ t('INBOX_MGMT.TEMPLATES.CREATE.PARAMETER_FORMAT') }}
      </label>
      <div class="flex gap-2" :class="{ 'opacity-60': isFormatLocked }">
        <button
          type="button"
          class="flex-1 h-9 rounded-lg text-sm font-medium border transition-colors"
          :class="
            formatVal === 'positional'
              ? 'bg-n-brand/10 border-n-brand text-n-brand'
              : 'border-n-weak text-n-slate-10 hover:bg-n-alpha-2'
          "
          :disabled="isFormatLocked"
          @click="formatVal = 'positional'"
        >
          {{ t('INBOX_MGMT.TEMPLATES.CREATE.FORMAT_POSITIONAL') }}
          <span class="block text-[10px] font-normal opacity-70">{{
            positionalHint
          }}</span>
        </button>
        <button
          type="button"
          class="flex-1 h-9 rounded-lg text-sm font-medium border transition-colors"
          :class="
            formatVal === 'named'
              ? 'bg-n-brand/10 border-n-brand text-n-brand'
              : 'border-n-weak text-n-slate-10 hover:bg-n-alpha-2'
          "
          :disabled="isFormatLocked"
          @click="formatVal = 'named'"
        >
          {{ t('INBOX_MGMT.TEMPLATES.CREATE.FORMAT_NAMED') }}
          <span class="block text-[10px] font-normal opacity-70">{{
            namedHint
          }}</span>
        </button>
      </div>
      <p v-if="isFormatLocked" class="text-[11px] text-n-slate-10 mt-1">
        {{ t('INBOX_MGMT.TEMPLATES.EDIT.FORMAT_LOCKED') }}
      </p>
      <p v-else class="text-[11px] text-n-slate-10 mt-1">
        {{ t('INBOX_MGMT.TEMPLATES.CREATE.PARAMETER_FORMAT_HELP') }}
      </p>
    </div>
  </div>
</template>
